// ===================================
// UPDATED MAIN APP COMPONENT WITH AI CONFIG PROVIDER
// src/App.tsx
// ENHANCED: Includes loading state, error boundary, and debug indicator
// ===================================
import React, { Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { SettingsProvider } from "./contexts/SettingsContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import { AIConfigProvider, useAIConfig } from "./contexts/AIConfigContext"; // NEW: Import both
import ErrorBoundary from "./components/ErrorBoundary";
import NotificationToast from "./components/NotificationToast";

// Pages
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Chat from "./pages/Chat";
import Search from "./pages/Search";
import Documents from "./pages/Documents";
import Profile from "./pages/Profile";
import Settings from "./pages/Settings";
import PublicChatPage from "./pages/PublicChatPage";
import PublicChatIframe from "./pages/PublicChatIframe";
import WidgetChat from "./pages/WidgetChat";
import AuthLogin from "./pages/AuthLogin";

// ===================================
// NEW: Loading Screen Component
// ===================================
const LoadingScreen: React.FC = () => (
  <div className="min-h-screen bg-slate-900 flex items-center justify-center">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
      <p className="text-slate-400">Loading...</p>
    </div>
  </div>
);

// ===================================
// NEW: AI Config Error Fallback
// ===================================
const AIConfigErrorFallback: React.FC<{ 
  error: Error; 
  resetErrorBoundary: () => void 
}> = ({ error, resetErrorBoundary }) => (
  <div className="min-h-screen bg-slate-900 flex items-center justify-center">
    <div className="max-w-md p-6 bg-slate-800 rounded-lg border border-red-500/50 text-center">
      <h2 className="text-xl font-semibold text-white mb-2">
        Failed to load AI configuration
      </h2>
      <p className="text-slate-400 mb-4">{error.message}</p>
      <button
        onClick={resetErrorBoundary}
        className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
      >
        Try Again
      </button>
    </div>
  </div>
);

// ===================================
// NEW: AI Config Debug Status (Development Only)
// Shows current AI config in bottom-right corner
// ===================================
// const AIConfigDebugStatus: React.FC = () => {
//   const { effectiveConfig, isLoading, error } = useAIConfig();

//   // Only show in development
//   if (process.env.NODE_ENV !== 'development') return null;

//   return (
//     <div className="fixed bottom-4 right-4 text-xs bg-slate-800 p-2 rounded shadow-lg border border-slate-700 z-50 max-w-xs">
//       <div className="text-slate-400">
//         <div className="font-semibold text-slate-300 mb-1">AI Config</div>
//         {isLoading && <span className="text-yellow-400">⏳ Loading...</span>}
//         {error && <span className="text-red-400">❌ {error}</span>}
//         {effectiveConfig && !isLoading && (
//           <div className="space-y-0.5">
//             <div className="text-green-400">
//               ✅ {effectiveConfig.llm_provider}
//             </div>
//             <div className="text-slate-500">
//               Model: {effectiveConfig.llm_model?.split('/').pop() || effectiveConfig.llm_model}
//             </div>
//             <div className="text-slate-500">
//               Mode: {effectiveConfig.mode}
//             </div>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// ===================================
// Route Components
// ===================================
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
};

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <>{children}</>;
};

// ===================================
// Main App Content (Inside All Providers)
// ===================================
const AppContent: React.FC = () => {
  // OPTIONAL: Uncomment to wait for AI config before rendering protected routes
  // const { isLoading: aiConfigLoading } = useAIConfig();
  // if (aiConfigLoading) {
  //   return <LoadingScreen />;
  // }

  return (
    <div className="App min-h-screen bg-gray-50">
      <Routes>
        {/* Public Routes - No AI Config needed */}
        <Route
          path="/support"
          element={
            <PublicRoute>
              <PublicChatPage />
            </PublicRoute>
          }
        />
        <Route
          path="/support-chatbot"
          element={
            <PublicRoute>
              <PublicChatIframe />
            </PublicRoute>
          }
        />
        <Route
          path="/widget"
          element={
            <PublicRoute>
              <WidgetChat />
            </PublicRoute>
          }
        />
        <Route
          path="/chat/public"
          element={
            <PublicRoute>
              <PublicChatPage />
            </PublicRoute>
          }
        />
        <Route
          path="/customer-service"
          element={
            <PublicRoute>
              <PublicChatPage />
            </PublicRoute>
          }
        />

        {/* Authentication */}
        <Route path="/login" element={<Login />} />
        <Route path="/autologin" element={<AuthLogin />} />

        {/* Protected Routes - AI Config Available */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/chat"
          element={
            <ProtectedRoute>
              <Chat />
            </ProtectedRoute>
          }
        />
        <Route
          path="/chat/:sessionId"
          element={
            <ProtectedRoute>
              <Chat />
            </ProtectedRoute>
          }
        />
        <Route
          path="/search"
          element={
            <ProtectedRoute>
              <Search />
            </ProtectedRoute>
          }
        />
        <Route
          path="/documents"
          element={
            <ProtectedRoute>
              <Documents />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/:tab"
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          }
        />

        {/* Default Routes */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      
      <NotificationToast />
      
      {/* NEW: Debug indicator (only shows in development) */}
      {/* <AIConfigDebugStatus /> */}
    </div>
  );
};

// ===================================
// Root App Component
// Provider Order:
// 1. ErrorBoundary - Catches all errors
// 2. AuthProvider - Authentication state
// 3. SettingsProvider - User settings
// 4. NotificationProvider - Toast notifications
// 5. AIConfigProvider - AI configuration (needs auth)
// 6. Router - Routing
// ===================================
function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <SettingsProvider>
          <NotificationProvider>
            <AIConfigProvider>
              <Router>
                <AppContent />
              </Router>
            </AIConfigProvider>
          </NotificationProvider>
        </SettingsProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;

// // ===================================
// // ALTERNATIVE: Conditional Provider (only for routes that need it)
// // ===================================
// /*
// If you want AI config only in specific routes to reduce API calls:

// function App() {
//   return (
//     <ErrorBoundary>
//       <AuthProvider>
//         <SettingsProvider>
//           <NotificationProvider>
//             <Router>
//               <Routes>
//                 {/* Public routes - no AI config */}
//                 <Route path="/login" element={<Login />} />
//                 <Route path="/support" element={<PublicChatPage />} />
                
//                 {/* Routes that need AI config - wrap individually */}
//                 <Route
//                   path="/chat/*"
//                   element={
//                     <AIConfigProvider>
//                       <ProtectedRoute>
//                         <Chat />
//                       </ProtectedRoute>
//                     </AIConfigProvider>
//                   }
//                 />
//                 <Route
//                   path="/settings/*"
//                   element={
//                     <AIConfigProvider>
//                       <ProtectedRoute>
//                         <Settings />
//                       </ProtectedRoute>
//                     </AIConfigProvider>
//                   }
//                 />
//               </Routes>
//             </Router>
//           </NotificationProvider>
//         </SettingsProvider>
//       </AuthProvider>
//     </ErrorBoundary>
//   );
// }
// */